import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { apiClient, extractApiError } from '../api/axios';
import { API } from '../constants/api';
import { DEFAULT_PAGE_SIZE } from '../constants/ui';

const initialState = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  modal: false,
  total: 0,
  loading: false,
  error: '',
  success: false,
  deleted: false,
  update: false,

  addSuccess: false,
  addError: '',
  addLoading: false,

  clients: [],
  newClient: null,
  billingAccounts: [],
  billingAccount: null,
  billingLoading: false,
  billingError: '',
  transactions: [],
  transactionsLoading: false,
  transactionsError: '',

  closeDebtLoading: false,
  closeDebtError: null,
  closeDebtSuccess: null,

  bottleHistory: [],
  bottleHistoryLoading: false,
  bottleHistoryError: '',

  clientBillingAccounts: [],
  clientBillingAccountsLoading: false,
  clientBillingAccountsError: '',
};

export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async ({ url, filter = {} }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(url, {
        params: { ...filter, page: filter.page || 1, page_size: filter.pageSize || DEFAULT_PAGE_SIZE },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const billingAccounts = createAsyncThunk('clients/billingAccounts', async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.billing.accounts);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const billingAccountById = createAsyncThunk('clients/billingAccountById', async (id, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.billing.transactions(id));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const addClient = createAsyncThunk('clients/addClient', async (data, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API.vendors.createClient, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const addCustomer = createAsyncThunk('clients/addCustomer', async (data, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API.customers.list, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

// Two entry points. If accountId is known, hit the detail endpoint. Otherwise
// fall through to close_for_client so the backend auto-creates the
// BillingAccount for a customer + vendor pair that has never transacted yet
// (e.g., admin created a client and only needs to fix return_bottles).
export const closeDebt = createAsyncThunk(
  'clients/closeDebt',
  async ({ accountId, customerId, vendorId, data }, { rejectWithValue }) => {
    try {
      let response;
      if (accountId) {
        response = await apiClient.post(API.billing.closeDebt(accountId), data);
      } else {
        response = await apiClient.post(API.billing.closeForClient, {
          customer_id: customerId,
          vendor_id: vendorId,
          ...data,
        });
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || extractApiError(error));
    }
  }
);

// Mark a VendorClient as verified (clears the "Tekshirilmagan" badge). The
// flag is never cleared automatically anywhere else — this action is the only
// way to remove it once auto-set by the order flow.
export const markClientVerified = createAsyncThunk(
  'clients/markClientVerified',
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API.vendors.markClientVerified(clientId));
      return { id: clientId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || extractApiError(error));
    }
  }
);

export const fetchBottleReturnHistory = createAsyncThunk(
  'clients/fetchBottleReturnHistory',
  async (accountId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API.billing.bottleReturnHistory(accountId));
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

// Mijozning billing account'larini olamiz.
// account.client ichida return_bottles, total_debt, total_balance bor —
// boshqa endpoint chaqirish shart emas.
export const fetchClientBillingAccounts = createAsyncThunk(
  'clients/fetchClientBillingAccounts',
  async (customerId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(API.billing.accountsByClient, {
        params: { client_id: customerId },
      });

      const accountsRaw = Array.isArray(res.data) ? res.data : res.data?.results || [];

      // Har account uchun return_bottles ni client ichidan olamiz
      return accountsRaw.map((acc) => ({
        ...acc,
        return_bottles:
          acc.return_bottles ??
          acc.client?.return_bottles ??
          0,
      }));
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const deleteClients = createAsyncThunk('clients/deleteClients', async ({ token, url }, { rejectWithValue }) => {
  try {
    const response = await apiClient.del(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchClients.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(fetchClients.fulfilled, (state, action) => {
      state.loading = false;
      state.page = action.payload.page || state.page;
      state.pageSize = action.payload.page_size;
      state.total = action.payload.total;
      state.clients = action.payload.results || action.payload;
      state.success = true;
    });
    builder.addCase(fetchClients.rejected, (state, action) => {
      state.error = action.payload;
      toast.error(action.payload);
      state.loading = false;
      state.success = false;
    });
    builder.addCase(billingAccounts.pending, (state) => {
      state.billingLoading = true;
      state.billingError = '';
    });
    builder.addCase(billingAccounts.fulfilled, (state, action) => {
      state.billingLoading = false;
      state.billingAccounts = action.payload;
    });
    builder.addCase(billingAccounts.rejected, (state, action) => {
      state.billingLoading = false;
      state.billingError = action.payload;
    });
    builder.addCase(billingAccountById.pending, (state) => {
      state.transactionsLoading = true;
      state.transactionsError = '';
    });
    builder.addCase(billingAccountById.fulfilled, (state, action) => {
      state.transactionsLoading = false;
      state.transactions = action.payload;
    });
    builder.addCase(billingAccountById.rejected, (state, action) => {
      state.transactionsLoading = false;
      state.transactionsError = action.payload;
    });

    builder.addCase(addClient.pending, (state) => {
      state.addLoading = true;
      state.addError = '';
    });
    builder.addCase(addClient.fulfilled, (state, action) => {
      state.addLoading = false;
      state.addSuccess = true;
      state.newClient = action.payload;
      toast.success("Mijoz qo'shildi");
      state.update = !state.update;
    });
    builder.addCase(addClient.rejected, (state, action) => {
      state.addError = action.payload;
      toast.error(action.payload);
      state.addLoading = false;
      state.addSuccess = false;
    });

    builder.addCase(addCustomer.pending, (state) => {
      state.addLoading = true;
      state.addError = '';
    });
    builder.addCase(addCustomer.fulfilled, (state, action) => {
      state.addLoading = false;
      state.addSuccess = true;
      state.newClient = action.payload;
      toast.success("Mijoz qo'shildi");
      state.update = !state.update;
    });
    builder.addCase(addCustomer.rejected, (state, action) => {
      state.addError = action.payload;
      toast.error(action.payload);
      state.addLoading = false;
      state.addSuccess = false;
    });

    builder.addCase(deleteClients.fulfilled, (state) => {
      state.deleted = true;
      state.update = !state.update;
    });
    builder.addCase(deleteClients.rejected, (state, action) => {
      toast.error(action.payload);
    });

    builder.addCase(closeDebt.pending, (state) => {
      state.closeDebtLoading = true;
      state.closeDebtError = null;
      state.closeDebtSuccess = null;
    });
    builder.addCase(closeDebt.fulfilled, (state, action) => {
      state.closeDebtLoading = false;
      state.closeDebtSuccess = action.payload;
      state.update = !state.update;
      // Match the toast to what actually happened. A pure bottle adjustment
      // shouldn't announce "Qarz yopildi" — that wording confuses vendors who
      // just fixed a container count.
      const billing = action.payload?.billing || {};
      const paidAmt = Number(billing.paid_amount || 0);
      if (paidAmt > 0) {
        toast.success('Qarz muvaffaqiyatli yopildi');
      } else {
        toast.success('Idish soni yangilandi');
      }
    });
    builder.addCase(closeDebt.rejected, (state, action) => {
      state.closeDebtLoading = false;
      state.closeDebtError = action.payload;
      const payload = action.payload;
      if (typeof payload === 'string') {
        toast.error(payload);
      } else if (payload?.non_field_errors?.[0]) {
        toast.error(payload.non_field_errors[0]);
      } else if (payload?.paid_amount?.[0]) {
        toast.error(payload.paid_amount[0]);
      } else if (payload?.returned_bottles?.[0]) {
        toast.error(payload.returned_bottles[0]);
      } else if (payload?.added_bottles?.[0]) {
        toast.error(payload.added_bottles[0]);
      } else if (payload?.message) {
        // close_for_client backend returns {status: "error", message: "..."}
        // for issues like missing IDs or unknown customer/vendor — surface
        // that instead of the generic fallback.
        toast.error(payload.message);
      } else {
        toast.error('Qarzni yopishda xatolik');
      }
    });

    builder.addCase(markClientVerified.fulfilled, (state, action) => {
      const { id } = action.payload || {};
      // Flip the flag in-place so the row loses its "Tekshirilmagan" chip
      // without waiting for a refetch. The list ordering stays stable.
      if (Array.isArray(state.clients) && id != null) {
        state.clients = state.clients.map((row) =>
          String(row.id) === String(id) ? { ...row, is_unverified: false } : row
        );
      }
      toast.success('Mijoz tekshirilgan deb belgilandi');
    });
    builder.addCase(markClientVerified.rejected, (state, action) => {
      const payload = action.payload;
      if (typeof payload === 'string') toast.error(payload);
      else if (payload?.detail) toast.error(payload.detail);
      else toast.error('Tekshirishda xatolik');
    });

    builder.addCase(fetchBottleReturnHistory.pending, (state) => {
      state.bottleHistoryLoading = true;
      state.bottleHistoryError = '';
    });
    builder.addCase(fetchBottleReturnHistory.fulfilled, (state, action) => {
      state.bottleHistoryLoading = false;
      state.bottleHistory = action.payload?.results || [];
    });
    builder.addCase(fetchBottleReturnHistory.rejected, (state, action) => {
      state.bottleHistoryLoading = false;
      state.bottleHistoryError = action.payload;
    });

    builder.addCase(fetchClientBillingAccounts.pending, (state) => {
      state.clientBillingAccountsLoading = true;
      state.clientBillingAccountsError = '';
      state.clientBillingAccounts = [];
    });
    builder.addCase(fetchClientBillingAccounts.fulfilled, (state, action) => {
      state.clientBillingAccountsLoading = false;
      state.clientBillingAccounts = action.payload || [];
    });
    builder.addCase(fetchClientBillingAccounts.rejected, (state, action) => {
      state.clientBillingAccountsLoading = false;
      state.clientBillingAccountsError = action.payload;
    });
  },
  reducers: {
    setBillingAccount: (state, { payload }) => {
      state.billingAccount = payload;
    },
    clearTransactions: (state) => {
      state.billingAccount = null;
      state.transactions = [];
      state.transactionsLoading = false;
      state.transactionsError = '';
    },
    resetSuccess: (state) => {
      state.addSuccess = false;
    },
    resetDeleted: (state) => {
      state.deleted = false;
    },
    resetCloseDebt: (state) => {
      state.closeDebtLoading = false;
      state.closeDebtError = null;
      state.closeDebtSuccess = null;
    },
    setModal: (state, action) => {
      state.modal = action.payload;
    },
    setUpdate: (state, action) => {
      state.update = action.payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
  },
});

export default clientsSlice.reducer;
export const {
  resetSuccess,
  resetDeleted,
  resetCloseDebt,
  setModal,
  setPage,
  setBillingAccount,
  clearTransactions,
} = clientsSlice.actions;
